/**
 * @file Region State Management
 * @description State management for region component
 */

// State object for region component
export const state = {
  regionDataCache: new Map(),
  originalRegionStyles: new Map(),
  tippyManager: null,
  eventManager: null,
  isInitialized: false,
};

/**
 * Store original styles for all regions
 * @param {string} selector - Selector for map regions
 */
export function storeOriginalStyles(selector) {
  if (state.originalRegionStyles.size > 0) return; // Already stored

  document.querySelectorAll(selector).forEach((region) => {
    const computedStyle = window.getComputedStyle(region);
    state.originalRegionStyles.set(region.id, {
      fill: region.getAttribute("fill") || computedStyle.fill,
      stroke: region.getAttribute("stroke") || computedStyle.stroke,
      opacity: region.getAttribute("opacity") || computedStyle.opacity,
      filter: region.getAttribute("filter") || computedStyle.filter,
      style: {
        fill: region.style.fill,
        stroke: region.style.stroke,
        opacity: region.style.opacity,
        filter: region.style.filter,
      },
    });
  });
}

/**
 * Get cached region data
 * @param {string} regionID - Region ID
 * @returns {Object|null} Cached region data
 */
export function getCachedRegionData(regionID) {
  return state.regionDataCache.get(regionID) || null;
}

/**
 * Set cached region data
 * @param {string} regionID - Region ID
 * @param {Object} data - Region data
 */
export function setCachedRegionData(regionID, data) {
  if (data) {
    state.regionDataCache.set(regionID, data);
  }
}

/**
 * Get original region styles
 * @param {string} regionID - Region ID
 * @returns {Object|null} Original styles
 */
export function getOriginalRegionStyles(regionID) {
  return state.originalRegionStyles.get(regionID) || null;
}

/**
 * Set tippy manager instance
 * @param {Object} tippyManager - Tippy manager instance
 */
export function setTippyManager(tippyManager) {
  state.tippyManager = tippyManager;
}

/**
 * Get tippy manager instance
 * @returns {Object|null} Tippy manager instance
 */
export function getTippyManager() {
  return state.tippyManager;
}

/**
 * Set event manager instance
 * @param {Object} eventManager - Event manager instance
 */
export function setEventManager(eventManager) {
  state.eventManager = eventManager;
}

/**
 * Get event manager instance
 * @returns {Object|null} Event manager instance
 */
export function getEventManager() {
  return state.eventManager;
}

/**
 * Set initialization status
 * @param {boolean} status - Initialization status
 */
export function setInitialized(status) {
  state.isInitialized = status;
}

/**
 * Get initialization status
 * @returns {boolean} Initialization status
 */
export function isInitialized() {
  return state.isInitialized;
}

/**
 * Clear all cached data
 */
export function clearCache() {
  state.regionDataCache.clear();
  state.originalRegionStyles.clear();
}

/**
 * Get state statistics
 * @returns {Object} State statistics
 */
export function getStateStats() {
  return {
    cachedRegions: state.regionDataCache.size,
    originalStyles: state.originalRegionStyles.size,
    isInitialized: state.isInitialized,
    hasTippyManager: !!state.tippyManager,
    hasEventManager: !!state.eventManager,
  };
}
